/**
 * A self-fetching SIM picker for the activation flow (§A PR-5) — the fix for
 * the user's actual complaint: `/sims/activate` used to read only the
 * selection made on the estate page, so the dashboard's "Activate" tile
 * landed on "No SIMs selected" — a dead end dressed as a screen.
 *
 * The picker fetches eligible SIMs itself, so activation is startable from
 * anywhere:
 *
 *   * the status filter defaults to `AVAILABLE` and is sent **server-side**
 *     via the existing `simStatus` parameter — the server does the narrowing,
 *     not a client-side sieve over one page;
 *   * every row is still client-checked with `isEligible`, because the wire
 *     spelling of a status is undocumented (§13.8) and a row the registry
 *     cannot vouch for gets a disabled checkbox and its status pill rather
 *     than a checkbox that will bounce at the API;
 *   * checkboxes write into the **same** `useSelection()` the estate uses —
 *     one selection, whichever screen built it, so the review step and the
 *     cap behave identically from either entry point;
 *   * the 100-SIM cap holds by the same mechanism as the estate: at the cap
 *     the next checkbox is disabled and `capMessage` says why — never a
 *     silent truncation.
 */

import type { SimSummary } from '@rd/shared';
import { useEffect, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import { Badge } from '../../../components/Badge.js';
import { EmptyState } from '../../../components/EmptyState.js';
import { ErrorState } from '../../../components/ErrorState.js';
import { Field } from '../../../components/Field.js';
import { Checkbox, Input, Select } from '../../../components/Input.js';
import { Pagination } from '../../../components/Pagination.js';
import { Table, type Column } from '../../../components/Table.js';
import { useSelection } from '../../inventory/selection.js';
import { useBaskets, useBasketSims, type SimFilterState } from '../../inventory/useInventory.js';
import { isEligible, normaliseStatus, type SimActionDef } from '../actions/actionRegistry.js';
import { simStatusLabel, simStatusTone } from '../statusTone.js';

export function EligibleSimPicker({
  action,
  onPick,
}: {
  /** The action whose `eligibleStatuses` drive the status filter and checks. */
  action: SimActionDef;
  /**
   * Called on every checkbox gesture, before the toggle. The flow uses it to
   * pin itself to the pick step: without it, the first checked row would flip
   * the automatic pick/review derivation into review mid-selection.
   */
  onPick?: () => void;
}): ReactElement {
  const selection = useSelection();
  const [params, setParams] = useSearchParams();

  const baskets = useBaskets();
  const [basketChoice, setBasketChoice] = useState<string | null>(null);
  const basketRef = basketChoice ?? baskets.data?.data[0]?.basketRef ?? null;

  // Defaults are visible, so they are sent from the first request on
  // (the `useFilterState` rule): the wire must agree with what the form shows.
  const [status, setStatus] = useState<string>(action.eligibleStatuses[0] ?? '');
  // MSISDN only — the gateway 400s on every other simFilterType.
  const [searchBy, setSearchBy] = useState<'MSISDN' | 'SIM_NO'>('MSISDN');
  const [searchValue, setSearchValue] = useState('');

  /** Any filter change is a new result set — `page=4` of the old one is a trap. */
  const clearPage = (): void => {
    if (!params.has('page')) return;
    const next = new URLSearchParams(params);
    next.delete('page');
    setParams(next, { replace: true });
  };

  const filters: SimFilterState = {
    simStatus: status || undefined,
    ...(searchValue ? { simFilterType: searchBy, filterValue: searchValue } : {}),
  };
  const sims = useBasketSims(basketRef, filters);
  const rows = sims.data?.data ?? [];
  // Bound once so the narrowing survives into the Field render callback.
  const basketRows = baskets.data?.data ?? [];

  // So `selectedSims()` can resolve full rows for the review step, exactly as
  // the estate page does for its own rows.
  useEffect(() => {
    selection.remember(rows);
  }, [rows, selection]);

  // One entry per status, whatever spelling variants the registry carries for
  // tone purposes — a dropdown offering TEST_MODE and TESTMODE reads as two.
  const statusChoices = [
    ...new Map(action.eligibleStatuses.map((s) => [normaliseStatus(s), s])).values(),
  ];

  const columns: Column<SimSummary>[] = [
    {
      key: 'select',
      header: 'Select',
      width: '3.5rem',
      render: (sim) => {
        const eligible = isEligible(action, sim);
        return (
          <Checkbox
            checked={selection.selected.has(sim.simRef)}
            // Ineligible rows keep a visible-but-dead checkbox next to their
            // status pill: the pill explains *why* it cannot be picked.
            disabled={!eligible || !selection.canSelect(sim.simRef)}
            aria-label={`Select ${sim.msisdn ?? sim.simRef}`}
            onChange={() => {
              onPick?.();
              selection.toggle(sim.simRef);
            }}
          />
        );
      },
    },
    {
      key: 'msisdn',
      header: 'Mobile number',
      kind: 'mono',
      render: (sim) => sim.msisdn ?? '—',
    },
    { key: 'iccid', header: 'SIM number', kind: 'mono', render: (sim) => sim.iccid ?? '—' },
    {
      key: 'status',
      header: 'Status',
      kind: 'status',
      width: '9rem',
      render: (sim) => <Badge tone={simStatusTone(sim.status)}>{simStatusLabel(sim.status)}</Badge>,
    },
  ];

  return (
    <div data-testid="sim-picker" className="flex flex-col gap-4">
      <form
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => e.preventDefault()}
        aria-label="Find SIMs to activate"
      >
        {basketRows.length > 1 ? (
          <Field label="Basket">
            {(props) => (
              <Select
                {...props}
                value={basketRef ?? ''}
                onChange={(e) => {
                  setBasketChoice(e.target.value);
                  clearPage();
                }}
              >
                {basketRows.map((b) => (
                  <option key={b.basketRef} value={b.basketRef}>
                    {b.basketName ?? b.basketRef} ({b.counts.total})
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        <Field label="Status">
          {(props) => (
            <Select
              {...props}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                clearPage();
              }}
            >
              {statusChoices.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              {/* No `simStatus` sent; rows are still eligibility-checked. */}
              <option value="">Any eligible</option>
            </Select>
          )}
        </Field>

        <Field label="Search by">
          {(props) => (
            <Select
              {...props}
              value={searchBy}
              onChange={(e) => {
                setSearchBy(e.target.value as 'MSISDN' | 'SIM_NO');
                if (searchValue) clearPage();
              }}
            >
              <option value="MSISDN">Mobile number</option>
              <option value="SIM_NO">SIM number</option>
            </Select>
          )}
        </Field>

        <Field label="Value">
          {(props) => (
            <Input
              {...props}
              value={searchValue}
              inputMode="numeric"
              onChange={(e) => {
                setSearchValue(e.target.value);
                clearPage();
              }}
            />
          )}
        </Field>
      </form>

      {sims.isError ? <ErrorState error={sims.error} onRetry={() => void sims.refetch()} /> : null}

      {sims.data || sims.isPending ? (
        <>
          <Table
            caption="SIMs eligible for this action"
            columns={columns}
            rows={rows}
            rowKey={(sim) => sim.simRef}
            loading={sims.isPending}
            isSelected={(sim) => selection.selected.has(sim.simRef)}
            empty={
              <EmptyState
                title="No SIMs to pick"
                body="Nothing in this basket matches the current filters. Try another status or basket."
              />
            }
          />
          {sims.data ? (
            <Pagination meta={sims.data.meta} onPageChange={sims.setPage} label="Eligible SIMs" />
          ) : null}
        </>
      ) : null}

      {selection.capMessage ? (
        <p role="status" data-testid="cap-message" className="text-sm text-warning">
          {selection.capMessage}
        </p>
      ) : null}
    </div>
  );
}
